import * as ReqType from '../types/ServerRequests';
import LoginServices from './LoginServices';
import Axios, { AxiosResponse } from 'axios';
import { AdresseService } from './AdresseService';
import { TypeEventService } from './TypeEventService';

/**
 * Cette classe permet la communication entre le backend et le frontend
 * Elle assure l'envois la reception des données entre le backend et frontend
 * Elle permet la creation, modification, et suppression des Evènements
 */
export class EventService {
    private static instance: EventService = new EventService();

    public static getInstance(): EventService {
        return this.instance;
    }

    /**
     * crée un évènement
     * @param {Event} newEvent
     * @returns {Promise<Event>}
     */
    public createEvent(newEvent: ReqType.Event) {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Promise.all([
                AdresseService.getInstance().createAdresse(newEvent.adresse),
            ]).then(
                (array) => {
                    var adresse: ReqType.Adresse = array[0];
                    var monEvent = {
                        budget: newEvent.budget,
                        cours_id: (newEvent.cours === null ? null : newEvent.cours.id),
                        date_heure_debut: newEvent.date_heure_debut.valueOf() / 1000,
                        date_heure_fin: newEvent.date_heure_fin.valueOf() / 1000,
                        description: newEvent.description,
                        type_events_id: newEvent.type_event.id,
                        adresse_id: adresse.id,
                        intitule: newEvent.intitule,
                    };
                    return (Axios.post(
                        process.env.REACT_APP_EVENT_CREATE_URL!,
                        monEvent,
                        {
                            headers: { 'X-Access-Token': token },
                        }
                    ).then(
                        (response) => {
                            newEvent.id = response.data.id;
                            newEvent.adresse = adresse;
                            return newEvent;
                        }
                    ));
                }
            )
        );
    }

    /**
     * edition des évènements
     * @param {Event} newEvent
     * @param {Event} oldEvent
     * @returns {Promise<Event>}
     */
    public editEvent(newEvent: ReqType.Event, oldEvent: ReqType.Event): Promise<ReqType.Event> {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Promise.all([
                AdresseService.getInstance().updateAdresse(newEvent.adresse, oldEvent.adresse)
            ]).then(
                (array) => {
                    var adresse: ReqType.Adresse = array[0];
                    var monEvent = {
                        budget: newEvent.budget,
                        cours_id: (newEvent.cours === null ? null : newEvent.cours.id),
                        date_heure_debut: newEvent.date_heure_debut.valueOf() / 1000,
                        date_heure_fin: newEvent.date_heure_fin.valueOf() / 1000,
                        description: newEvent.description,
                        type_event_id: newEvent.type_event.id,
                        adresse_id: adresse.id,
                        intitule: newEvent.intitule,
                    };
                    return (Axios.put(
                        process.env.REACT_APP_EVENT_UPDATE_URL! + '&id=' + oldEvent.id,
                        monEvent,
                        {
                            headers: { 'X-Access-Token': token },
                        }
                    ).then(
                        (response) => {
                            return newEvent;
                        }
                    ));
                }
            )
        );
    }

    /**
     * supression d'un évènement
     * @param {Event} event
     * @returns {Promise<null>}
     */
    public deleteEvent(event: ReqType.Event): Promise<null> {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Axios.delete(
                process.env.REACT_APP_EVENT_DELETE_URL! + '&id=' + event.id,
                {
                    headers: { 'X-Access-Token': token },
                }
            ).then(
                (response) => {
                    return AdresseService.getInstance().deleteAdresse(event.adresse);
                }
            )
        );
    }

    /**
     * retourne une liste d'évènement
     * @returns {Promise<Event[]>}
     */
    public getEvents(): Promise<ReqType.Event[]> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_EVENT_LIST_URL!,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Event[]>) => {
                return response.data.map(
                    (e: ReqType.Event) => {
                        e.date_heure_debut = new Date(e.date_heure_debut);
                        e.date_heure_fin = new Date(e.date_heure_fin);
                        return e;
                    }
                );
            }
        );
    }

    /**
     * retourne un evenement à partir de l'identifiant des l'evenement
     * @param {number} id
     * @returns {Promise<Event>}
     */
    public getEvent(id: number): Promise<ReqType.Event> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_EVENT_VIEW_URL! + '&id=' + id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Event>) => {
                var event = response.data;
                event.date_heure_debut = new Date(event.date_heure_debut);
                event.date_heure_fin = new Date(event.date_heure_fin);
                return response.data;
            }
        );
    }

    /**
     * retourne les frais liés à un évènement
     * @param {number} id
     * @returns {Promise<Frais[]>}
     */
    public getEventFees(id: number): Promise<ReqType.Frais[]> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_EVENT_FEES_URL! + '&id=' + id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Frais[]>) => {
                return response.data;
            }
        );
    }

    /**
     * retourne l'evenement par defaut
     * @returns {Event}
     */
    public getDefaultEvent(): ReqType.Event {
        const retVal: ReqType.Event = {
            id: -1,
            intitule: '',
            type_event: TypeEventService.getInstance().getDefaultTypeEvent(),
            adresse: AdresseService.getInstance().getDefaultAdresse(),
            cours: null,
            date_heure_debut: new Date(),
            date_heure_fin: new Date(),
            description: '',
            budget: '0.0',
            media: [],
        };
        return retVal;
    }

    /**
     * duplique un evenement
     * @param {Event} origin
     * @returns {Event}
     */
    public duplicateEvent(origin: ReqType.Event): ReqType.Event {
        const dateDebut: Date = new Date(origin.date_heure_debut);
        const dateFin: Date = new Date(origin.date_heure_fin);
        const retVal: ReqType.Event = JSON.parse(JSON.stringify(origin));
        retVal.date_heure_debut = dateDebut;
        retVal.date_heure_fin = dateFin;
        return retVal;
    }

    /**
     * ajout d'un évènement
     * @param {Frais} fee
     * @returns {AxiosPromise<any>}
     */
    public addEventFee(fee: ReqType.Frais) {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.post(
            process.env.REACT_APP_ADD_EVENT_FEE_URL! + '&id=' + fee.event_id,
            fee,
            {
                headers: { 'X-Access-Token': token },
            }
        );
    }

    /**
     * suppression d'un évènement
     * @param {Frais} fee
     * @returns {AxiosPromise}
     */
    public deleteEventFee(fee: ReqType.Frais) {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.delete(
            process.env.REACT_APP_DELETE_EVENT_FEE_URL! + '&id=' + fee.id,
            {
                headers: { 'X-Access-Token': token },
            }
        );
    }

    /**
     * ajout d'un participant à un évènement
     * @param {UserView} user
     * @param {Event} event
     * @returns {Promise<Presence>}
     */
    public addParticipant(user: ReqType.UserView, event: ReqType.Event): Promise<ReqType.Presence> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_ADD_EVENT_ATTENDANT_URL! + '&userid=' + user.id + '&eventid=' + event.id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (resp: AxiosResponse) => {
                return (
                    {
                        id: resp.data.id,
                        user_id: resp.data.user_id,
                        event_id: resp.data.event_id,
                        presence: resp.data.presence === 1,
                        user: resp.data.user,
                    }
                );
            }
        );
    }

    /**
     * suppression d'un participant à un évènement
     * @param {Presence} p
     * @returns {AxiosPromise<any>}
     */
    public removeParticipant(p: ReqType.Presence) {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_REMOVE_EVENT_ATTENDANT_URL! + '&userid=' + p.user.id + '&eventid=' + p.event_id,
            {
                headers: { 'X-Access-Token': token },
            }
        );
    }

    /**
     * retourne la liste des participants d'un évènement à partir de id de l'évènement
     * @param {number} id
     * @returns {Promise<Presence[]>}
     */
    public getParticipants(id: number): Promise<ReqType.Presence[]> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_EVENT_GET_ATTENDANT_URL! + '&eventid=' + id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (rep: AxiosResponse) => {
                const arr = rep.data.map(
                    (data: {
                        id: number;
                        user_id: number;
                        event_id: number;
                        presence: number;
                        user: ReqType.UserMe;
                    }
                    ) => {
                        return (
                            {
                                id: data.id,
                                user_id: data.user_id,
                                event_id: data.event_id,
                                presence: data.presence === 1,
                                user: data.user,
                            }
                        );
                    }
                );
                return arr;
            }
        );
    }

    /**
     * confirmation de la participation à un evenement
     * @param {number} eventid
     * @param {boolean} bool
     * @returns {Promise<void>}
     */
    public confirmParticipation(eventid: number, bool: boolean): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_EVENT_CONFIRM_URL! + '&eventid=' + eventid + '&bool=' + (bool ? '1' : '0'),
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (rep: AxiosResponse) => {
                return ;
            }
        );
    }
}


// WEBPACK FOOTER //
// ./src/services/EventService.tsx